import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  IconButton,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
} from '@mui/icons-material';
import { MainLayout } from '../layouts/MainLayout';
import { lawService, type LawArticle } from '../services/api/lawService';
import type { Law, Country, Bookmark } from '../types';

// Law categories
const lawCategories = [
  { id: 'all', name: '全て' },
  { id: 'company', name: '会社法関連' },
  { id: 'financial', name: '金融商品取引法関連' },
  { id: 'antitrust', name: '独占禁止法関連' },
  { id: 'labor', name: '労働法関連' },
  { id: 'tax', name: '税法関連' },
  { id: 'foreign_exchange', name: '外為法関連' },
];

const countryLabels: Record<Country, string> = {
  japan: '日本',
  usa: '米国',
  uk: '英国',
  eu: 'EU',
  china: '中国',
};

export function LawDatabasePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLawId, setSelectedLawId] = useState<string | null>(null);

  // API state
  const [laws, setLaws] = useState<Law[]>([]);
  const [selectedLaw, setSelectedLaw] = useState<Law | null>(null);
  const [articles, setArticles] = useState<LawArticle[]>([]);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);

  // Loading states
  const [isLoadingLaws, setIsLoadingLaws] = useState(true);
  const [isLoadingLawDetail, setIsLoadingLawDetail] = useState(false);
  const [isLoadingBookmarks, setIsLoadingBookmarks] = useState(true);

  // Error states
  const [lawsError, setLawsError] = useState<string | null>(null);
  const [lawDetailError, setLawDetailError] = useState<string | null>(null);
  const [bookmarkError, setBookmarkError] = useState<string | null>(null);

  // Helper functions to check bookmarks
  const isLawBookmarked = useCallback(
    (lawId: string): boolean => {
      return bookmarks.some((b) => b.lawId === lawId && !b.articleNumber);
    },
    [bookmarks]
  );

  const isArticleBookmarked = useCallback(
    (lawId: string, articleNumber: string): boolean => {
      return bookmarks.some((b) => b.lawId === lawId && b.articleNumber === articleNumber);
    },
    [bookmarks]
  );

  const getBookmarkId = useCallback(
    (lawId: string, articleNumber?: string): string | undefined => {
      const bookmark = bookmarks.find(
        (b) => b.lawId === lawId && b.articleNumber === (articleNumber || undefined)
      );
      return bookmark?.id;
    },
    [bookmarks]
  );

  // Fetch laws
  const fetchLaws = useCallback(async () => {
    setIsLoadingLaws(true);
    setLawsError(null);
    try {
      const params: { query?: string; category?: string } = {};
      if (searchQuery) params.query = searchQuery;
      if (selectedCategory !== 'all') params.category = selectedCategory;

      const result = await lawService.searchLaws(params);
      setLaws(result);

      // Select first law if none selected
      if (result.length > 0 && !selectedLawId) {
        setSelectedLawId(result[0].id);
      }
    } catch (error) {
      setLawsError(error instanceof Error ? error.message : '法令の取得に失敗しました');
    } finally {
      setIsLoadingLaws(false);
    }
  }, [searchQuery, selectedCategory, selectedLawId]);

  // Fetch law detail and articles
  const fetchLawDetail = useCallback(async (lawId: string) => {
    setIsLoadingLawDetail(true);
    setLawDetailError(null);
    setSelectedLaw(null);
    setArticles([]);

    try {
      const [law, lawArticles] = await Promise.all([
        lawService.getLaw(lawId),
        lawService.getLawArticles(lawId),
      ]);
      setSelectedLaw(law);
      setArticles(lawArticles);
    } catch (error) {
      setLawDetailError(error instanceof Error ? error.message : '法令詳細の取得に失敗しました');
    } finally {
      setIsLoadingLawDetail(false);
    }
  }, []);

  // Fetch bookmarks
  const fetchBookmarks = useCallback(async () => {
    setIsLoadingBookmarks(true);
    try {
      const result = await lawService.getBookmarks();
      setBookmarks(result);
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
    } finally {
      setIsLoadingBookmarks(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchLaws();
    fetchBookmarks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch law detail when selection changes
  useEffect(() => {
    if (selectedLawId) {
      fetchLawDetail(selectedLawId);
    }
  }, [selectedLawId, fetchLawDetail]);

  // Toggle law bookmark
  const toggleBookmark = async (lawId: string) => {
    setBookmarkError(null);
    try {
      if (isLawBookmarked(lawId)) {
        const bookmarkId = getBookmarkId(lawId);
        if (bookmarkId) {
          await lawService.removeBookmark(bookmarkId);
          setBookmarks((prev) => prev.filter((b) => b.id !== bookmarkId));
        }
      } else {
        const newBookmark = await lawService.addBookmark(lawId);
        setBookmarks((prev) => [...prev, newBookmark]);
      }
    } catch (error) {
      setBookmarkError(error instanceof Error ? error.message : 'ブックマーク操作に失敗しました');
    }
  };

  // Toggle article bookmark
  const toggleArticleBookmark = async (lawId: string, articleNumber: string) => {
    setBookmarkError(null);
    try {
      if (isArticleBookmarked(lawId, articleNumber)) {
        const bookmarkId = getBookmarkId(lawId, articleNumber);
        if (bookmarkId) {
          await lawService.removeBookmark(bookmarkId);
          setBookmarks((prev) => prev.filter((b) => b.id !== bookmarkId));
        }
      } else {
        const newBookmark = await lawService.addBookmark(lawId, articleNumber);
        setBookmarks((prev) => [...prev, newBookmark]);
      }
    } catch (error) {
      setBookmarkError(error instanceof Error ? error.message : 'ブックマーク操作に失敗しました');
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetchLaws();
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  // Re-fetch when category changes
  useEffect(() => {
    fetchLaws();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  return (
    <MainLayout>
      <Box>
        {/* Header & Search */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" component="h1" fontWeight={500} sx={{ mb: 2 }}>
            法令データベース
          </Typography>

          <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', gap: 1 }}>
            <TextField
              placeholder="法令名またはキーワードで検索"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              startIcon={<SearchIcon />}
              disabled={isLoadingLaws}
            >
              検索
            </Button>
          </Box>
        </Box>

        {/* Category Tabs */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          {lawCategories.map((cat) => (
            <Chip
              key={cat.id}
              label={cat.name}
              color={selectedCategory === cat.id ? 'primary' : 'default'}
              variant={selectedCategory === cat.id ? 'filled' : 'outlined'}
              onClick={() => handleCategoryChange(cat.id)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>

        {/* Error Alert */}
        {(lawsError || bookmarkError) && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => {
            setLawsError(null);
            setBookmarkError(null);
          }}>
            {lawsError || bookmarkError}
          </Alert>
        )}

        {/* Two-column Layout */}
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            height: 'calc(100vh - 320px)',
            minHeight: 400,
          }}
        >
          {/* Left Column: Law List */}
          <Paper
            sx={{
              width: 320,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                {isLoadingLaws ? '読み込み中...' : `${laws.length}件の法令`}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {isLoadingLaws ? (
                <Box sx={{ p: 2 }}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Skeleton variant="text" width="80%" />
                      <Skeleton variant="text" width="60%" />
                    </Box>
                  ))}
                </Box>
              ) : (
                <List disablePadding>
                  {laws.map((law) => (
                    <ListItemButton
                      key={law.id}
                      selected={selectedLawId === law.id}
                      onClick={() => setSelectedLawId(law.id)}
                      sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        '&.Mui-selected': {
                          borderLeft: 3,
                          borderLeftColor: 'primary.main',
                        },
                      }}
                    >
                      <ListItemText
                        primary={<Typography variant="subtitle2">{law.title}</Typography>}
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {law.lawNumber}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  ))}
                  {laws.length === 0 && (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography color="text.secondary">該当する法令が見つかりません</Typography>
                    </Box>
                  )}
                </List>
              )}
            </Box>
          </Paper>

          {/* Right Column: Law Content */}
          <Paper
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {isLoadingLawDetail ? (
              <Box sx={{ p: 3 }}>
                <Skeleton variant="text" width="50%" height={32} />
                <Skeleton variant="text" width="30%" sx={{ mb: 3 }} />
                <Skeleton variant="rectangular" height={100} sx={{ mb: 2 }} />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
                <Skeleton variant="text" width="80%" />
              </Box>
            ) : lawDetailError ? (
              <Box sx={{ p: 3 }}>
                <Alert severity="error">{lawDetailError}</Alert>
              </Box>
            ) : selectedLaw ? (
              <>
                {/* Law Header */}
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography variant="h6">{selectedLaw.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedLaw.lawNumber}｜{countryLabels[selectedLaw.country]}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => toggleBookmark(selectedLaw.id)}
                    color={isLawBookmarked(selectedLaw.id) ? 'warning' : 'default'}
                    disabled={isLoadingBookmarks}
                  >
                    {isLawBookmarked(selectedLaw.id) ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                  </IconButton>
                </Box>

                {/* Law Content */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                  {/* Articles from API */}
                  {articles.length > 0 ? (
                    articles.map((article) => (
                      <Box key={article.id} sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2" color="primary.main">
                            {article.title || `第${article.articleNumber}条`}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() =>
                              toggleArticleBookmark(selectedLaw.id, article.articleNumber)
                            }
                            color={
                              isArticleBookmarked(selectedLaw.id, article.articleNumber)
                                ? 'warning'
                                : 'default'
                            }
                            disabled={isLoadingBookmarks}
                          >
                            {isArticleBookmarked(selectedLaw.id, article.articleNumber) ? (
                              <BookmarkIcon fontSize="small" />
                            ) : (
                              <BookmarkBorderIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}
                        >
                          {article.content}
                        </Typography>
                      </Box>
                    ))
                  ) : selectedLaw.content ? (
                    // Fallback to law content if no articles
                    selectedLaw.content.split('\n\n').map((paragraph, index) => {
                      const isArticleHeader = paragraph.startsWith('第');

                      if (isArticleHeader) {
                        const articleNumber = index.toString();
                        return (
                          <Box key={index} sx={{ mb: 3 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                mb: 1,
                              }}
                            >
                              <Typography variant="subtitle2" color="primary.main">
                                {paragraph.split('\n')[0]}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  toggleArticleBookmark(selectedLaw.id, articleNumber)
                                }
                                color={
                                  isArticleBookmarked(selectedLaw.id, articleNumber)
                                    ? 'warning'
                                    : 'default'
                                }
                                disabled={isLoadingBookmarks}
                              >
                                {isArticleBookmarked(selectedLaw.id, articleNumber) ? (
                                  <BookmarkIcon fontSize="small" />
                                ) : (
                                  <BookmarkBorderIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}
                            >
                              {paragraph.split('\n').slice(1).join('\n')}
                            </Typography>
                          </Box>
                        );
                      }

                      return (
                        <Typography
                          key={index}
                          variant="body2"
                          sx={{ mb: 2, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}
                        >
                          {paragraph}
                        </Typography>
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      法令本文はe-Gov法令APIから取得されます。
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">
                  左のリストから法令を選択してください
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </MainLayout>
  );
}
